import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { MODULE_CATALOGUE, SUSPEND_CATEGORIES, SUSPEND_CATEGORY_LABELS, formatMoney, isAddOnModule, type AddOnModule, type EntitlementGridRow, type LifecycleDto, type LifecycleState, type SuspendCategory } from '@his/shared';
import { get, post, put } from '@/lib/api';
import { dateOnly, humanize } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { PageHeader, StatTile, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Field, PageSkeleton, Input, StatusBadge } from '@/components/ui/primitives';
import { TenantDangerZone, TenantUsersPanel } from './TenantAdminPanels';
import { TenantBranchesPanel } from './TenantBranchesPanel';
import { TenantAccountPanel } from './TenantAccountPanel';
import { TenantHistorySharingPanel } from './TenantHistorySharingPanel';
import { TenantBillingPanel } from './TenantBillingPanel';
import { TenantPlansPanel, type HeldPlan } from './TenantPlansPanel';
import { TenantDeploymentPanel } from './TenantDeploymentPanel';
import { AddOnToggles } from './AddOnToggles';
import { ConsequencePreview } from './ConsequencePreview';
import { TenantClosurePanel, type ClosureFacts } from './TenantClosurePanel';
import type { AddOnState } from './add-ons';
import { OwnerConfirmDialog } from './OwnerConfirm';
import { useOwnerCapability } from './owner-nav';
import { FacilityGroupDialog } from './FacilityGroupDialog';
import { CopyMastersDialog } from './CopyMastersDialog';
import { AuditTable } from './OwnerAuditPage';
import { lifecycleChip } from './TenantsPage';
import { moduleName } from '../admin/org-types';

const TAB_ALIASES: Record<string, string> = { modules: 'licence', usage: 'summary', danger: 'data' };
const TAB_IDS = ['summary', 'licence', 'account', 'sharing', 'branches', 'users', 'deployment', 'data', 'activity'];

function tabFrom(value: string | null): string {
  const id = value ? TAB_ALIASES[value] ?? value : 'summary';
  return TAB_IDS.includes(id) ? id : 'summary';
}

interface TenantDetail extends ClosureFacts {
  id: string;
  name: string;
  slug: string;
  status: string;
  parentTenantId: string | null;
  shareHistoryWithinGroup: boolean;
  shareHistoryAcrossPlatform: boolean;
  readOnlyGrace: boolean;
  directoryEnabled: boolean;
  timezone: string;
  locale: string;
  currency: string;
  billingCurrency?: string | null;
  customDomain: string | null;
  createdAt: string;
  trialEndsAt: string | null;
  suspendedAt?: string | null;
  suspendCategory?: SuspendCategory | null;
  environment?: 'live' | 'demo';
  lifecycleState?: LifecycleState;
  lifecycle?: LifecycleDto | null;
  facilityType: string;
  priceMinor: number;
  billingCycle: string;
  setupFeeMinor: number;
  limits: { users: number | null; beds: number | null; branches: number | null; storageMb: number | null; smsCredits: number | null };
  plan: { id: string; name: string; moduleFlags: string[] } | null;
  plans: HeldPlan[];
  usage: { users: number; branches: number; beds: number; patientsLast30d: number; invoicesLast30d: number };
  subscriptionEndsAt: string | null;
  contact: { id: string; name: string; email: string; phone: string | null } | null;
}

const GROUP_LABELS: Record<string, string> = {
  core: 'Core',
  clinical: 'Clinical',
  diagnostics: 'Diagnostics',
  commerce: 'Pharmacy, billing & finance',
  admin: 'Administration',
  patient: 'Patient facing',
};

/**
 * One tenant: its commercial state and the module toggle grid (SRS 5.5).
 *
 * The grid is the Owner's main lever — OPD, IPD and Pharmacy sell separately
 * and in any combination. Dependencies lock a toggle rather than hiding it, and
 * the lock explains itself, so the Owner is never left guessing why a switch
 * will not move.
 */
export function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab = tabFrom(params.get('tab'));
  const setTab = (id: string) => setParams({ tab: id }, { replace: true });
  const [grouping, setGrouping] = useState(false);
  const [copying, setCopying] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [freezing, setFreezing] = useState(false);
  const [changingEnvironment, setChangingEnvironment] = useState(false);
  const [toggling, setToggling] = useState<EntitlementGridRow | null>(null);
  const canToggleModules = useOwnerCapability('tenant.entitlement.update');
  const canSuspend = useOwnerCapability('tenant.suspend');
  const canFreeze = useOwnerCapability('tenant.freeze');
  const canChangeEnvironment = useOwnerCapability('tenant.environment.update');
  const canClose = useOwnerCapability('tenant.closure');
  const canExport = useOwnerCapability('tenant.export');
  const canEditProfile = useOwnerCapability('tenant.profile.update');
  const canSeeUsers = useOwnerCapability('tenant.user.read');
  const canSeeDeployment = useOwnerCapability('deployment.read');
  const canGroup = useOwnerCapability('tenant.group.update');
  const canCopy = useOwnerCapability('tenant.copy_masters');
  const canSeeActivity = useOwnerCapability('platform_audit.read');
  const canSeeDanger = [
    useOwnerCapability('tenant.directory.update'),
    useOwnerCapability('tenant.subscription.update'),
    useOwnerCapability('tenant.archive'),
  ].some(Boolean);

  const tenant = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => get<TenantDetail>(`/platform/tenants/${tenantId}`),
    enabled: Boolean(tenantId),
  });

  const entitlements = useQuery({
    queryKey: ['entitlements', tenantId],
    queryFn: () => get<EntitlementGridRow[]>(`/platform/tenants/${tenantId}/entitlements`),
    enabled: Boolean(tenantId),
  });

  const health = useQuery({
    queryKey: ['tenant-health', tenantId],
    queryFn: () => get<{ score: number; signals: Record<string, number> }>(`/platform/tenants/${tenantId}/health`),
    enabled: Boolean(tenantId),
  });

  const requestedAction = params.get('action');
  useEffect(() => {
    if (!requestedAction || !tenant.data) return;
    if (requestedAction === 'suspend' && canSuspend) setSuspending(true);
    if (requestedAction === 'reactivate' && canSuspend) setReactivating(true);
    if (requestedAction === 'freeze' && canFreeze) setFreezing(true);
    setParams({ tab: tabFrom(params.get('tab')) }, { replace: true });
  }, [requestedAction, tenant.data, canSuspend, canFreeze, params, setParams]);

  const askToToggle = (row: EntitlementGridRow) => {
    if (canToggleModules) setToggling(row);
  };

  if (tenant.isLoading) {
    return (
      <PageSkeleton />
    );
  }
  if (!tenant.data) return <Alert variant="destructive">That tenant could not be opened.</Alert>;

  const t = tenant.data;
  const packageRows = (entitlements.data ?? []).filter((row) => !isAddOnModule(row.moduleCode));
  const addOnRows = (entitlements.data ?? []).filter((row) => isAddOnModule(row.moduleCode));
  const addOnStates: AddOnState[] = addOnRows.map((row) => ({
    code: row.moduleCode as AddOnModule,
    name: row.name,
    description: MODULE_CATALOGUE.find((module) => module.code === row.moduleCode)?.description ?? '',
    on: row.status === 'enabled',
    problem: row.lockedReason,
  }));
  const groups = [...new Set(packageRows.map((row) => row.group))];

  return (
    <>
      <PageHeader
        title={t.name}
        description={`${t.slug} · ${t.currency} · ${t.timezone} · Created ${dateOnly(t.createdAt)}`}
        actions={
          <>
            <StatusBadge status={t.status} />
            {/* Worth seeing without opening the danger zone: a hospital that
                cannot be found by patients usually does not know it. */}
            {!t.directoryEnabled && <Badge variant="warning">Off the directory</Badge>}
            {canEditProfile && (
              <Button variant="secondary" onClick={() => navigate(`/owner/tenants/${t.id}/edit`)}>
                Edit
              </Button>
            )}
            {/* An archived facility is already fully shut, so offering to
                suspend it says nothing and hides the only thing worth doing:
                bringing it back. The restore itself lives in the danger zone,
                behind a reason and an authenticator code. */}
            {t.environment === 'demo' && <Badge variant="secondary">Demo</Badge>}
            {canChangeEnvironment && (
              <Button variant="ghost" size="sm" onClick={() => setChangingEnvironment(true)}>
                {t.environment === 'demo' ? 'Mark live' : 'Mark demo'}
              </Button>
            )}
            {canFreeze && !t.suspendedAt && t.status !== 'archived' && (
              <Button variant="outline" onClick={() => setFreezing(true)}>
                Freeze
              </Button>
            )}
            {!canSuspend ? null : t.status === 'archived' ? (
              <Button onClick={() => setTab('data')}>Restore…</Button>
            ) : t.suspendedAt ? (
              <Button onClick={() => setReactivating(true)}>Reactivate</Button>
            ) : (
              <Button variant="destructive" onClick={() => setSuspending(true)}>
                Suspend
              </Button>
            )}
          </>
        }
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatTile label="Plan" value={t.plan?.name ?? 'None'} sub={`${formatMoney(t.priceMinor, t.billingCurrency ?? t.currency)} / ${humanize(t.billingCycle, 'billing_cycle')}`} />
        <StatTile
          label="Health"
          value={health.data ? `${health.data.score}/100` : '—'}
          tone={health.data && health.data.score < 50 ? 'warning' : 'success'}
        />
        <StatTile label="Users" value={t.usage.users} />
        <StatTile label="Patients (30d)" value={t.usage.patientsLast30d} />
        <StatTile label="Invoices (30d)" value={t.usage.invoicesLast30d} />
      </div>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'summary', label: 'Summary' },
          { id: 'licence', label: 'Licence' },
          { id: 'account', label: 'Account' },
          { id: 'sharing', label: 'Group & sharing' },
          { id: 'branches', label: 'Branches' },
          ...(canSeeUsers ? [{ id: 'users', label: 'Users & access' }] : []),
          ...(canSeeDeployment ? [{ id: 'deployment', label: 'Deployment' }] : []),
          ...(canSeeDanger || canClose || canExport || canCopy ? [{ id: 'data', label: 'Data' }] : []),
          ...(canSeeActivity ? [{ id: 'activity', label: 'Activity' }] : []),
        ]}
      />

      {tab === 'licence' && (
        <div className="mb-4">
          <TenantPlansPanel
            tenantId={t.id}
            slug={t.slug}
            held={t.plans ?? []}
            addOns={addOnRows.filter((row) => row.status === 'enabled').map((row) => row.moduleCode)}
          />
        </div>
      )}

      {tab === 'licence' && (
        <>
          <Alert className="mb-4">
            Disabling a module hides it from a cutoff date but never deletes data — history stays readable in
            reports, and re-enabling restores full access.
          </Alert>

          {entitlements.isLoading && (
            <PageSkeleton />
          )}

          <div className="space-y-4">
            {addOnStates.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Add-on services</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Not part of any package. Switch one on only when this account has bought it; switching it off keeps every record and stops it
                    being used.
                  </p>
                  <AddOnToggles
                    states={addOnStates}
                    readOnly={!canToggleModules}
                    busyCode={toggling?.moduleCode ?? null}
                    onToggle={(state) => {
                      const row = addOnRows.find((entry) => entry.moduleCode === state.code);
                      if (row) askToToggle(row);
                    }}
                  />
                </CardContent>
              </Card>
            )}
            {groups.map((group) => (
              <Card key={group}>
                <CardHeader>
                  <CardTitle className="text-sm">{GROUP_LABELS[group] ?? humanize(group)}</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {packageRows
                    .filter((row) => row.group === group)
                    .map((row) => (
                      <ModuleToggle
                        key={row.moduleCode}
                        row={row}
                        busy={toggling?.moduleCode === row.moduleCode}
                        readOnly={!canToggleModules}
                        onToggle={() => askToToggle(row)}
                      />
                    ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {tab === 'summary' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Usage against this facility’s limits</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">State</span>
              <Badge variant={lifecycleChip({ status: t.status, lifecycle: t.lifecycle ?? null, closureEndsAt: t.closureEndsAt ?? null, trialEndsAt: t.trialEndsAt }).variant}>
                {lifecycleChip({ status: t.status, lifecycle: t.lifecycle ?? null, closureEndsAt: t.closureEndsAt ?? null, trialEndsAt: t.trialEndsAt }).label}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Packages</span>
              <span>{(t.plans ?? []).map((held) => held.name).join(', ') || t.plan?.name || 'None'}</span>
            </div>
            <UsageRow label="Users" value={t.usage.users} />
            <UsageRow label="Branches" value={t.usage.branches} />
            <UsageRow label="Beds" value={t.usage.beds} />
            <UsageRow label="Patients registered (30 days)" value={t.usage.patientsLast30d} />
            <UsageRow label="Invoices raised (30 days)" value={t.usage.invoicesLast30d} />
            {health.data && (
              <div className="mt-3 rounded-md border p-3">
                <p className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">Health signals</p>
                {Object.entries(health.data.signals).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-muted-foreground">{humanize(key)}</span>
                    <span className="tabular">{Math.round(value)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'account' && (
        <div className="space-y-4">
          <TenantAccountPanel
            tenantId={t.id}
            contact={t.contact}
            subscriptionEndsAt={t.subscriptionEndsAt}
          />
          <TenantBillingPanel
            tenantId={t.id}
            slug={t.slug}
            currency={t.billingCurrency ?? t.currency}
            billing={{
              priceMinor: t.priceMinor ?? 0,
              billingCycle: t.billingCycle ?? 'monthly',
              setupFeeMinor: t.setupFeeMinor ?? 0,
              limits: t.limits ?? { users: null, beds: null, branches: null, storageMb: null, smsCredits: null },
            }}
          />
        </div>
      )}

      {tab === 'sharing' && (
        <div className="space-y-4">
          {canGroup && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Facility group</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="text-muted-foreground">
                  {t.parentTenantId ? 'Part of a group with a flagship facility.' : 'Not part of a group, or heads one.'} Group members must be the same type and environment.
                </span>
                <Button variant="outline" size="sm" onClick={() => setGrouping(true)}>
                  Change group…
                </Button>
              </CardContent>
            </Card>
          )}
          <TenantHistorySharingPanel
            tenantId={t.id}
            slug={t.slug}
            sharing={{
              withinGroup: t.shareHistoryWithinGroup ?? false,
              acrossPlatform: t.shareHistoryAcrossPlatform ?? false,
              hasGroup: Boolean(t.parentTenantId),
            }}
          />
        </div>
      )}

      {tab === 'branches' && <TenantBranchesPanel tenantId={t.id} />}

      {tab === 'users' && canSeeUsers && <TenantUsersPanel tenantId={t.id} />}

      {tab === 'deployment' && canSeeDeployment && <TenantDeploymentPanel tenantId={t.id} tenantName={t.name} />}

      {tab === 'data' && canCopy && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-sm">Copy data from another facility</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-muted-foreground">Masters only — catalogues, price lists and templates, never patients.</span>
            <Button variant="outline" size="sm" onClick={() => setCopying(true)}>
              Copy data from…
            </Button>
          </CardContent>
        </Card>
      )}

      {tab === 'data' && (canClose || canExport) && (
        <div className="mb-4">
          <TenantClosurePanel facts={t} />
        </div>
      )}

      {tab === 'data' && canSeeDanger && (
        <TenantDangerZone
          tenantId={t.id}
          slug={t.slug}
          status={t.status}
          directoryEnabled={t.directoryEnabled ?? true}
        />
      )}

      {tab === 'activity' && canSeeActivity && <AuditTable tenantId={t.id} />}

      {grouping && <FacilityGroupDialog tenant={t} open={grouping} onClose={() => setGrouping(false)} />}

      {copying && <CopyMastersDialog tenant={t} open={copying} onClose={() => setCopying(false)} />}

      {suspending && (
        <SuspendDialog
          open={suspending}
          tenantId={t.id}
          slug={t.slug}
          onClose={() => setSuspending(false)}
          onDone={() => {
            setSuspending(false);
            void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
          }}
        />
      )}

      {freezing && (
        <FreezeDialog
          open
          tenantId={t.id}
          slug={t.slug}
          onClose={() => setFreezing(false)}
          onDone={() => {
            setFreezing(false);
            void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
          }}
        />
      )}

      {changingEnvironment && (
        <EnvironmentDialog
          open
          tenantId={t.id}
          current={t.environment ?? 'live'}
          onClose={() => setChangingEnvironment(false)}
          onDone={() => {
            setChangingEnvironment(false);
            void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
          }}
        />
      )}

      {reactivating && (
        <OwnerConfirmDialog
          open
          onClose={() => setReactivating(false)}
          title="Reactivate this facility"
          description={t.slug}
          confirmLabel="Reactivate"
          successMessage="Facility reactivated"
          warning={<>Everyone at this facility can sign in and write again as soon as you confirm.</>}
          action={(payload) => post(`/platform/tenants/${t.id}/reactivate`, payload)}
          onDone={() => {
            setReactivating(false);
            void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
          }}
        />
      )}

      {toggling && (
        <OwnerConfirmDialog
          open
          onClose={() => setToggling(null)}
          title={`${toggling.status === 'enabled' ? 'Switch off' : 'Switch on'} ${toggling.name}`}
          description={t.slug}
          confirmLabel={toggling.status === 'enabled' ? 'Switch off' : 'Switch on'}
          destructive={toggling.status === 'enabled'}
          warning={
            toggling.status === 'enabled' ? (
              <>History stays readable and nothing is deleted; switching it back on restores full access.</>
            ) : undefined
          }
          successMessage={`${toggling.name} ${toggling.status === 'enabled' ? 'switched off' : 'switched on'}`}
          action={(payload) =>
            put<EntitlementGridRow[]>(`/platform/tenants/${t.id}/entitlements`, {
              ...payload,
              moduleCode: toggling.moduleCode,
              status: toggling.status === 'enabled' ? 'disabled' : 'enabled',
            })
          }
          onDone={(grid) => {
            queryClient.setQueryData(['entitlements', tenantId], grid);
            setToggling(null);
          }}
        />
      )}
    </>
  );
}

function ModuleToggle({
  row,
  busy,
  readOnly,
  onToggle,
}: {
  row: EntitlementGridRow;
  busy: boolean;
  readOnly: boolean;
  onToggle: () => void;
}) {
  const locked = Boolean(row.lockedReason);
  const enabled = row.status === 'enabled';

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={locked || busy || readOnly}
      title={row.lockedReason ?? undefined}
      className={`flex items-start gap-3 rounded-md border p-3 text-start transition-colors focus-ring ${
        enabled ? 'border-primary/40 bg-primary/5' : 'bg-background'
      } ${locked ? 'cursor-not-allowed opacity-60' : 'hover:bg-accent/50'}`}
    >
      <span
        aria-hidden
        className={`mt-0.5 flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors ${
          enabled ? 'bg-primary' : 'bg-muted-foreground/30'
        }`}
      >
        <span
          className={`h-4 w-4 rounded-full bg-white transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0'}`}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="font-medium">{row.name}</span>
          {locked && <Lock className="h-3 w-3 text-muted-foreground" />}
          {row.source === 'plan' && enabled && <Badge variant="muted">Plan</Badge>}
        </span>
        {row.lockedReason ? (
          <span className="mt-0.5 block text-xs text-muted-foreground">{row.lockedReason}</span>
        ) : row.dependsOn.length > 1 ? (
          <span className="mt-0.5 block text-xs text-muted-foreground">
            Needs {row.dependsOn.filter((d) => d !== 'core').map(moduleName).join(', ')}
          </span>
        ) : null}
      </span>
    </button>
  );
}

function UsageRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between border-b py-1 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular font-medium">{value}</span>
    </div>
  );
}

function SuspendDialog({
  open,
  tenantId,
  slug,
  onClose,
  onDone,
}: {
  open: boolean;
  tenantId: string;
  slug: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [readOnly, setReadOnly] = useState(true);
  const [category, setCategory] = useState<SuspendCategory>('non_payment');
  const [typedSlug, setTypedSlug] = useState('');
  const blockedConfirmed = readOnly || typedSlug.trim().toLowerCase() === slug.toLowerCase();

  return open ? (
    <OwnerConfirmDialog
      open={open}
      onClose={onClose}
      title="Suspend this facility"
      description={`${slug} · takes effect immediately for every user`}
      confirmLabel="Suspend"
      destructive
      extraValid={blockedConfirmed}
      successMessage="Facility suspended"
      action={(payload) =>
        post(`/platform/tenants/${tenantId}/suspend`, {
          ...payload,
          readOnlyGrace: readOnly,
          mode: readOnly ? 'read_only' : 'blocked',
          category,
          ...(readOnly ? {} : { confirmSlug: typedSlug.trim() }),
        })
      }
      onDone={onDone}
    >
      <ConsequencePreview tenantId={tenantId} />
      <Field label="Reason shown to the facility">
        <ChoiceButtons
          layout="chips"
          value={category}
          onChange={setCategory}
          options={SUSPEND_CATEGORIES.map((code) => ({ value: code, label: SUSPEND_CATEGORY_LABELS[code] }))}
        />
      </Field>
      <Checkbox
        checked={readOnly}
        onCheckedChange={setReadOnly}
        label="Leave the facility able to read its records"
        description="Recommended. Clinical access should not stop because an invoice is late; writes are blocked either way. Unticked, only its Super Admins can sign in."
      />
      {!readOnly && (
        <Field label={`Type ${slug} to block everyone else`}>
          <Input value={typedSlug} onChange={(event) => setTypedSlug(event.target.value)} autoComplete="off" />
        </Field>
      )}
    </OwnerConfirmDialog>
  ) : null;
}

function FreezeDialog({ open, tenantId, slug, onClose, onDone }: { open: boolean; tenantId: string; slug: string; onClose: () => void; onDone: () => void }) {
  const [category, setCategory] = useState<'security' | 'compliance'>('security');
  return open ? (
    <OwnerConfirmDialog
      open={open}
      onClose={onClose}
      title="Emergency freeze"
      description={`${slug} · read-only at once; only the Owner can lift it`}
      warning={<>The facility sees &quot;Access is limited while we check a security issue — contact support&quot; and who froze it. At most three freezes a day per account.</>}
      confirmLabel="Freeze"
      destructive
      successMessage="Facility frozen"
      action={(payload) => post(`/platform/tenants/${tenantId}/freeze`, { ...payload, category })}
      onDone={onDone}
    >
      <ConsequencePreview tenantId={tenantId} />
      <Field label="Kind of check">
        <ChoiceButtons
          value={category}
          onChange={setCategory}
          options={[
            { value: 'security', label: 'Security' },
            { value: 'compliance', label: 'Compliance' },
          ]}
        />
      </Field>
    </OwnerConfirmDialog>
  ) : null;
}

function EnvironmentDialog({
  open,
  tenantId,
  current,
  onClose,
  onDone,
}: {
  open: boolean;
  tenantId: string;
  current: 'live' | 'demo';
  onClose: () => void;
  onDone: () => void;
}) {
  const next = current === 'live' ? 'demo' : 'live';
  return open ? (
    <OwnerConfirmDialog
      open={open}
      onClose={onClose}
      title={next === 'demo' ? 'Mark as a demo facility' : 'Mark as a live facility'}
      description={
        next === 'demo'
          ? 'A demo is left out of revenue and renewals and cannot run offline. Refused once it has any patient, invoice or visit.'
          : 'A live facility counts towards revenue and renewals.'
      }
      confirmLabel={next === 'demo' ? 'Mark as demo' : 'Mark as live'}
      successMessage="Environment changed"
      action={(payload) => put(`/platform/tenants/${tenantId}/environment`, { ...payload, environment: next })}
      onDone={onDone}
    />
  ) : null;
}
